import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { Customer } from '../../models/customer.model';
import { GeneralService } from '../../services/general.service';
import { TmsName, TmsCredentialKeys, URL_PATTERN, DmsSpecialKeys } from '../../constants/tms.constants';
import { DEFAULT_DOC_TYPE_SOURCE } from '../../constants/doc-type.constants';
import { DocType } from '../../models/doc-type.model';
import { SettingsFields, SettingsFieldDef } from '../../constants/settings.constants';
import { FieldMappingTemplate } from '../../models/template.model';
import { ApiClient } from '../../models/api-client.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-customers',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './customers.component.html',
  styleUrl: './customers.component.scss',
})
export class CustomersComponent implements OnInit {
  customers: Customer[] = [];
  filterActive: boolean | null = null;
  showCreateForm = false;
  editingCustomer: Customer | null = null;
  error = '';
  success = '';
  creating: boolean = false;
  updating: boolean = false;
  isInitialLoading: boolean = true;
  isLoading: boolean = false;
  deleting: { [id: string]: boolean } = {};
  togglingStatus: { [id: string]: boolean } = {};
  tmsOptions: TmsName[] = Object.values(TmsName);
  readonly customerTypeOptions = ['Carrier', 'Broker'];
  dmsTemplates: FieldMappingTemplate[] = [];
  dmsApiClients: ApiClient[] = [];
  docTypeMappingsList: { source: string; target: string }[] = [];
  docMappingExpanded = false;
  credentialsExpanded = true;
  settingsExpanded = true;
  wfaiDocTypes: DocType[] = [];

  formData: Customer = this.emptyForm();

  constructor(
    private apiService: ApiService,
    private generalService: GeneralService,
  ) {}

  ngOnInit() {
    this.loadCustomers();
    this.apiService.getTemplates().subscribe({
      next: (r) => {
        if (r.success && r.data) this.dmsTemplates = r.data.templates;
      },
    });
    this.apiService.getApiClients().subscribe({
      next: (r) => {
        if (r.success && r.data) this.dmsApiClients = r.data.apiClients;
      },
    });
    this.apiService.getDocTypes().subscribe({
      next: (r) => {
        if (r.success && r.data) this.wfaiDocTypes = r.data.docTypes;
      },
    });
  }

  loadCustomers() {
    this.isInitialLoading = true;
    this.apiService.getCustomers(this.filterActive ?? undefined).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.customers = response.data.customers;
        } else {
          this.customers = [];
        }
        this.isInitialLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load customers';
        console.error(err);
        this.isInitialLoading = false;
      },
    });
  }

  toggleCreateForm() {
    this.showCreateForm = !this.showCreateForm;
    this.editingCustomer = null;
    this.formData = this.emptyForm();
    this.docTypeMappingsList = [];
    this.docMappingExpanded = false;
    this.credentialsExpanded = true;
    this.settingsExpanded = true;
    this.clearMessages();
  }

  startEdit(customer: Customer) {
    this.editingCustomer = customer;
    this.showCreateForm = true;
    this.clearMessages();

    this.formData = {
      ...this.emptyForm(),
      ...customer,
      lastSyncTime: customer.lastSyncTime ?? new Date().toISOString(),
    };
    this.formData.settings = customer.settings ?? {};
    this.docTypeMappingsList = this.dictToMappingList(customer.docTypeMappings);
    this.docMappingExpanded = this.docTypeMappingsList.length > 0;
    this.credentialsExpanded = false;
    this.settingsExpanded = false;
    this.applySettingsDefaults();
    setTimeout(() => {
      document.querySelector('.form-card')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }
  saveCustomer() {
    this.clearMessages();
    const payload = this.CustomerPayload();
    const isUpdate = !!this.editingCustomer;

    if (isUpdate) {
      this.updating = true;

      this.apiService.updateCustomer(this.editingCustomer!.customerId, payload).subscribe({
        next: async (response) => {
          if (response.success) {
            // Wait until the success Swal is closed
            await this.generalService.success('Customer updated successfully');
            this.cancelEditAndReload();
          } else {
            const msg = response.errors?.join(', ') || response.message || 'Failed to update customer';
            this.generalService.error(msg);
          }
        },
        error: (err) => {
          const msg = err.error?.message || err.message || 'Network error while updating customer';
          this.generalService.error(msg);
          this.updating = false;
        },
        complete: () => {
          this.updating = false;
        },
      });
    } else {
      this.creating = true;

      this.apiService.createCustomer(payload).subscribe({
        next: async (response) => {
          if (response.success) {
            await this.generalService.success('Customer created successfully');
            this.cancelEditAndReload();
          } else {
            const msg = response.errors?.join(', ') || response.message || 'Failed to create customer';
            this.generalService.error(msg);
          }
        },
        error: (err) => {
          const msg = err.error?.message || err.message || 'Network error while creating customer';
          this.generalService.error(msg);
          this.creating = false;
        },
        complete: () => {
          this.creating = false;
        },
      });
    }
  }

  private cancelEditAndReload() {
    this.cancelEdit();
    this.loadCustomers();
  }

  private getFormattedTime(date?: string): string {
    const d = date ? new Date(date) : new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');

    return (
      d.getFullYear().toString() +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      pad(d.getHours()) +
      pad(d.getMinutes()) +
      pad(d.getSeconds()) +
      '-0000'
    );
  }
  private CustomerPayload(): Customer {
    const credentials = { ...this.formData.credentials };

    if (this.formData.tmsName === TmsName.DMS) {
      credentials[DmsSpecialKeys.TransformerBaseUrl] = this.transformerBaseUrl;
    }

    const tmsName = (Object.values(TmsName) as string[]).includes(this.formData.tmsName) ? this.formData.tmsName : '';

    return {
      customerId: (this.formData.customerId || '').substring(0, 50),
      customerName: this.formData.customerName || '',
      customerType: this.formData.customerType || null,
      tmsName: tmsName,
      lastSyncTime: this.formData.lastSyncTime || this.getFormattedTime(),
      updateOrInsertStatuses: this.formData.updateOrInsertStatuses || '',
      updateOnlyStatuses: this.formData.updateOnlyStatuses || null,
      credentials: credentials,
      settings: this.buildSettingsPayload(),
      syncFrequencyMinutes: this.formData.syncFrequencyMinutes,
      orderRetentionDays: this.formData.orderRetentionDays,
      enabled: this.formData.enabled ?? true,
      outboundEnabled: this.formData.outboundEnabled ?? true,
      tonuCode: this.formData.tonuCode || null,
      whiteListedOrders: this.formData.whiteListedOrders || null,
      syncBatchSize: this.formData.syncBatchSize ?? null,
      docTypeMappings: this.mappingListToDict(this.docTypeMappingsList),
    };
  }

  // --- Settings helpers ---

  /** Guaranteed-non-null settings object for two-way binding under strictTemplates. */
  get settings(): { [key: string]: string } {
    if (!this.formData.settings) this.formData.settings = {};
    return this.formData.settings as { [key: string]: string };
  }

  /** Settings fields applicable to the currently selected TMS and customer type. */
  getCurrentSettingsFields(): SettingsFieldDef[] {
    return SettingsFields.filter(
      (f) => f.tmsName === this.formData.tmsName && (!f.customerType || f.customerType === this.formData.customerType),
    );
  }

  /** Re-apply defaults for the now-applicable settings; called when TMS or type changes. */
  onTmsOrTypeChange() {
    this.applySettingsDefaults();
  }

  isSettingChecked(field: SettingsFieldDef): boolean {
    return this.settings[field.key] === (field.checkedValue ?? 'true');
  }

  setSettingChecked(field: SettingsFieldDef, checked: boolean) {
    this.settings[field.key] = checked ? (field.checkedValue ?? 'true') : (field.uncheckedValue ?? 'false');
  }

  private applySettingsDefaults() {
    const settings = this.settings;
    for (const f of this.getCurrentSettingsFields()) {
      if (f.default !== undefined && (settings[f.key] === undefined || settings[f.key] === null)) {
        settings[f.key] = f.default;
      }
    }
  }

  /** Drop blank values so we don't persist empty settings; null when nothing remains. */
  private buildSettingsPayload(): { [key: string]: string } | null {
    const out: { [key: string]: string } = {};
    for (const [key, value] of Object.entries(this.formData.settings ?? {})) {
      if (value !== null && value !== undefined && `${value}`.trim() !== '') out[key] = value;
    }
    return Object.keys(out).length ? out : null;
  }

  // --- Export document mapping helpers ---

  addDocTypeMapping() {
    this.docMappingExpanded = true;
    this.docTypeMappingsList.push({ source: '', target: '' });
  }

  removeDocTypeMapping(index: number) {
    this.docTypeMappingsList.splice(index, 1);
  }

  /** Sources already chosen in other rows, so the dropdown can hide duplicates. */
  isSourceTaken(source: string, currentIndex: number): boolean {
    if (!source) return false;
    return this.docTypeMappingsList.some((m, i) => i !== currentIndex && m.source === source);
  }

  private dictToMappingList(dict?: { [source: string]: string } | null): { source: string; target: string }[] {
    if (!dict) return [];
    return Object.entries(dict).map(([source, target]) => ({ source, target }));
  }

  /** Convert the editable list back into the API dictionary, dropping incomplete rows. */
  private mappingListToDict(list: { source: string; target: string }[]): { [source: string]: string } | null {
    const dict: { [source: string]: string } = {};
    for (const { source, target } of list) {
      const key = (source || '').trim();
      const value = (target || '').trim();
      if (key && value) dict[key] = value;
    }
    return Object.keys(dict).length ? dict : null;
  }

  toggleStatus(customer: Customer) {
    const newStatus = !customer.enabled;
    const actionText = newStatus ? 'activate' : 'deactivate';

    this.generalService
      .confirm({
        title: `${newStatus ? 'Activate' : 'Deactivate'} Customer`,
        text: `Are you sure you want to ${actionText} this customer?`,
        confirmText: newStatus ? 'Yes, Activate' : 'Yes, Deactivate',
        confirmColor: newStatus ? '#28a745' : '#28a745',
        icon: 'question',
      })
      .then((result) => {
        if (!result.isConfirmed) return;

        this.togglingStatus[customer.customerId] = true;
        console.log('customer.customerId', customer.customerId);

        this.apiService.setCustomerStatus(customer.customerId, newStatus).subscribe({
          next: (response) => {
            if (response.success) {
              customer.enabled = newStatus;
              this.generalService.success(`Customer ${actionText}d successfully`);
            } else {
              const msg = response.errors?.join(', ') || `Failed to ${actionText} customer`;
              this.generalService.error(msg);
            }
          },
          error: (err) => {
            this.generalService.error(`Failed to ${actionText} customer: ${err.message || err}`);
            this.togglingStatus[customer.customerId] = false;
          },
          complete: () => {
            this.togglingStatus[customer.customerId] = false;
          },
        });
      });
  }

  deleteCustomer(customerId: string) {
    this.generalService
      .confirm({
        title: 'Delete Customer',
        text: 'Are you sure you want to delete this customer?',
        confirmText: 'Yes, Delete',
        confirmColor: '#e74c3c',
        icon: 'warning',
      })
      .then((result) => {
        if (!result.isConfirmed) return;

        this.deleting[customerId] = true;

        this.apiService.deleteCustomer(customerId).subscribe({
          next: () => {
            this.generalService.success('Customer has been deleted.').then(() => {
              this.loadCustomers();
            });
          },
          error: (err) => {
            this.generalService.error('Failed to delete customer: ' + (err.message || err));
            this.deleting[customerId] = false;
          },
          complete: () => {
            this.deleting[customerId] = false;
          },
        });
      });
  }

  cancelEdit() {
    this.creating = false;
    this.updating = false;
    this.editingCustomer = null;
    this.showCreateForm = false;
    this.formData = this.emptyForm();
    this.docTypeMappingsList = [];
    this.docMappingExpanded = false;
    this.credentialsExpanded = true;
    this.settingsExpanded = true;
    this.clearMessages();
  }

  private clearMessages() {
    this.error = '';
    this.success = '';
  }

  private emptyForm(): Customer {
    return {
      customerId: '',
      customerName: '',
      tmsName: '',
      lastSyncTime: new Date().toISOString(),
      enabled: true,
      outboundEnabled: false,
      credentials: {},
      settings: {},
    };
  }

  getCurrentTmsKeys(): string[] {
    return this.tmsCredentialKeys[this.formData.tmsName as TmsName] ?? [];
  }

  readonly tmsCredentialKeys = TmsCredentialKeys;
  readonly urlPattern = URL_PATTERN;
  readonly transformerBaseUrl = environment.apiUrl.startsWith('http')
    ? environment.apiUrl
    : window.location.origin + environment.apiUrl;

  isDmsCredentialRequired(_key: string): boolean {
    return this.formData.tmsName === TmsName.DMS;
  }

  isUrlField(key: string): boolean {
    return key.toLowerCase().includes('url');
  }

  isTemplateDropdown(key: string): boolean {
    return key === DmsSpecialKeys.TemplateId;
  }

  isApiClientDropdown(key: string): boolean {
    return key === DmsSpecialKeys.ApiClientId;
  }

  isNumericField(key: string): boolean {
    return key === DmsSpecialKeys.TemplateVersion;
  }

  isTransformerBaseUrlField(key: string): boolean {
    return key === DmsSpecialKeys.TransformerBaseUrl;
  }

  private static readonly CREDENTIAL_PLACEHOLDERS: Record<string, string> = {
    'mcleod-api-path': 'ws or api1',
    'mcleod-tenant-id': 'Cloud customers only',
    'mcleod-api-key': 'Cloud customers only',
  };

  getCredentialPlaceholder(key: string): string {
    return CustomersComponent.CREDENTIAL_PLACEHOLDERS[key] ?? '';
  }

  readonly DmsSpecialKeys = DmsSpecialKeys;
  readonly defaultDocTypeSource = DEFAULT_DOC_TYPE_SOURCE;

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
